import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Star, Truck } from "lucide-react";
import { BRAND, IMAGES, POSITIONS, PAY, BENEFITS, PARTNERS, TESTIMONIALS } from "@/lib/data";
import { SectionHeading, Overline } from "@/components/Section";
import Stats from "@/components/Stats";
import GrossPayCalculator from "@/components/GrossPayCalculator";
import FindMyLane from "@/components/FindMyLane";
import LocalPositionsMap from "@/components/LocalPositionsMap";
import VideoTestimonials from "@/components/VideoTestimonials";
import SEOHead from "@/components/SEOHead";

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://hylascompanion.com/#website",
      "name": "Hylas Companion Trucking",
      "url": "https://hylascompanion.com",
      "description": "Hylas Companion recruits CDL-A OTR truck drivers across all 48 states.",
      "inLanguage": "en-US",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://hylascompanion.com/positions?q={search_term_string}",
        },
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "Organization",
      "@id": "https://hylascompanion.com/#organization",
      "name": "Hylas Companion Trucking",
      "legalName": "Hylas Companion LLC",
      "url": "https://hylascompanion.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/4723859dfc697053410c88fca6f1c41af1f0aa1c43fb8f500fdcd4f43fe0f00e.jpeg",
        "width": 512,
        "height": 512,
      },
      "description": "Hylas Companion recruits CDL-A OTR truck drivers across all 48 states. Company driver, lease purchase, and local W2 positions available.",
      "telephone": "(708) 395-9565",
      "email": "contact@hylascompanion.com",
      "areaServed": "US",
      "sameAs": [
        "https://www.linkedin.com/company/hylascompanion",
      ],
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "(708) 395-9565",
          "contactType": "Recruiting",
          "availableLanguage": "English",
          "areaServed": "US",
        },
        {
          "@type": "ContactPoint",
          "email": "contact@hylascompanion.com",
          "contactType": "Customer Support",
          "availableLanguage": "English",
        },
      ],
      "knowsAbout": ["OTR trucking", "CDL-A driver jobs", "flatbed trucking", "reefer trucking", "lease purchase trucks"],
    },
    {
      "@type": "LocalBusiness",
      "@id": "https://hylascompanion.com/#localbusiness",
      "name": "Hylas Companion Trucking",
      "url": "https://hylascompanion.com",
      "telephone": "(708) 395-9565",
      "email": "contact@hylascompanion.com",
      "priceRange": "$$$",
      "currenciesAccepted": "USD",
      "paymentAccepted": "Direct Deposit",
      "areaServed": [
        "Illinois", "Indiana", "Wisconsin", "Michigan", "Ohio",
        "Colorado", "Maryland", "Pennsylvania", "Tennessee", "Texas",
        "Georgia", "North Carolina", "Virginia", "California", "Washington",
      ],
    },
    {
      "@type": "WebPage",
      "@id": "https://hylascompanion.com/#webpage",
      "url": "https://hylascompanion.com",
      "name": "OTR Truck Driver Jobs | CDL-A Hiring Nationwide | Hylas Companion Trucking",
      "isPartOf": { "@id": "https://hylascompanion.com/#website" },
      "about": { "@id": "https://hylascompanion.com/#organization" },
      "description": "Hylas Companion is hiring OTR, Flatbed, and Reefer CDL-A truck drivers in all 48 states. Company driver at 85¢+ CPM, team $1 CPM, 88% lease purchase, local W2 up to $2,500/wk. No fees, ever.",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", "[data-speakable='true']"],
      },
      "inLanguage": "en-US",
    },
  ],
};

export default function Home() {
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const vid = videoRef.current;
      const sec = videoSectionRef.current;
      if (!vid || !sec) return;
      const rect = sec.getBoundingClientRect();
      const viewH = window.innerHeight;
      if (rect.bottom < 0 || rect.top > viewH) return;
      // progress 0 → section enters bottom; 1 → section leaves top
      const progress = (viewH - rect.top) / (viewH + rect.height);
      // shift range ±50px — video moves slower than the page (parallax depth)
      const shift = (progress - 0.5) * -100;
      vid.style.transform = `translate3d(0, ${shift}px, 0)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div data-testid="home-page">
      <SEOHead
        title="OTR Truck Driver Jobs | CDL-A Hiring Nationwide"
        description="Hylas Companion is hiring OTR, Flatbed, and Reefer CDL-A truck drivers in Chicago, Denver, Baltimore, and all 48 states. $1 CPM team driving, 88% lease purchase, local W2 up to $2,500/wk. Apply today — no fees, no gimmicks."
        keywords="OTR truck driver jobs, CDL-A jobs, flatbed driver jobs, reefer driver, truck driver jobs Chicago IL, truck driver jobs Denver CO, company truck driver, lease purchase trucking, owner operator positions, top trucking pay"
        path="/"
        schema={HOME_SCHEMA}
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Parallax via CSS background-attachment:fixed — reliable on all desktop browsers */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${IMAGES.heroTruck})`,
            backgroundSize: "cover",
            backgroundPosition: "center 55%",
            backgroundAttachment: "fixed",
          }}
        />
        {/* Warm overlay: left side text area stays readable, right shows truck + sky */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/15" />
        {/* Top strip: dark-to-transparent so navbar text is readable at any brightness */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/35 to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-3xl fade-up">
            <Overline>All 48 States · New Trucks · Top Pay</Overline>
            <h1 data-speakable="true" className="font-head text-5xl sm:text-6xl md:text-8xl font-bold uppercase tracking-[0.15em] md:tracking-[0.25em] leading-[1.05] mt-6 text-[#0A0A0A]">
              {BRAND.short}
            </h1>
            <p className="font-head text-lg md:text-2xl uppercase tracking-[0.35em] text-[#0A0A0A]/70 mt-3">
              Companion
            </p>
            <p className="mt-8 text-2xl md:text-4xl font-extrabold tracking-tight text-[#0A0A0A]">
              {BRAND.tagline}
            </p>
            <p className="mt-5 text-base md:text-lg text-foreground/70 max-w-xl leading-relaxed">
              Nine pay packages. New trucks. No hiring area restriction. From $1 CPM team driving to local W2 — we have a lane for every CDL-A driver. Routes running Chicago, Denver, Baltimore, Phoenix, Oklahoma, and coast to coast.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/apply"
                data-testid="hero-apply-btn"
                className="inline-flex items-center justify-center gap-2 font-head text-base uppercase tracking-[0.2em] bg-primary text-black px-9 py-5 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300"
              >
                Apply Now <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/pay-benefits"
                className="inline-flex items-center justify-center gap-2 font-head text-base uppercase tracking-[0.2em] border-2 border-[#0A0A0A] text-[#0A0A0A] px-9 py-5 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300"
              >
                See The Pay
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section className="bg-[#0A0A0A] py-8 overflow-hidden border-y border-white/10">
        <div className="flex marquee whitespace-nowrap">
          {[...PARTNERS, ...PARTNERS, ...PARTNERS].map((p, i) => (
            <span key={i} className="font-head text-lg md:text-xl uppercase tracking-[0.3em] text-white/40 mx-10 flex items-center gap-3">
              <Truck className="w-4 h-4 text-primary" /> {p}
            </span>
          ))}
        </div>
      </section>

      {/* POSITIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="home-positions">
        <SectionHeading overline="Your Lane, Your Way" title="Positions We Run" subtitle="Nine pay packages. One for every driver, from first day to fleet owner." />
        <div className="grid md:grid-cols-2 gap-px bg-border mt-14 border border-border">
          {POSITIONS.map((p, i) => (
            <div key={p.key} className="bg-white p-8 md:p-10 group hover:bg-[#0A0A0A] transition-colors duration-300">
              <div className="flex items-start justify-between">
                <span className="font-head text-xs tracking-[0.3em] text-muted-foreground group-hover:text-primary transition-colors">0{i + 1}</span>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-head text-2xl md:text-3xl uppercase tracking-[0.1em] mt-6 text-[#0A0A0A] group-hover:text-white transition-colors">{p.title}</h3>
              <p className="mt-4 text-foreground/70 group-hover:text-white/70 transition-colors leading-relaxed">{p.blurb}</p>
              <ul className="mt-6 space-y-2">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-3 text-sm text-foreground/80 group-hover:text-white/80 transition-colors">
                    <Check className="w-4 h-4 text-primary shrink-0" /> {pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* PAY BANNER */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="home-pay">
        <div className="absolute inset-0">
          <img src={IMAGES.sunsetHighway} alt="OTR truck driver route at sunset — Hylas Companion runs all 48 states" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading overline="Top Pay" title="Pay That Respects The Miles" dark />
          <div className="grid md:grid-cols-3 gap-px bg-white/10 mt-14 border border-white/10">
            {PAY.map((p) => (
              <div key={p.trailer} className="bg-[#0A0A0A] p-10 text-center">
                <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50">{p.trailer}</p>
                <div className="font-head text-5xl md:text-6xl font-bold text-primary mt-4">{p.cpm}<span className="text-xl text-white/70 ml-1">{p.unit}</span></div>
                <p className="mt-4 text-white/60 text-sm">{p.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-white/40 text-xs text-center max-w-xl mx-auto">
            Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.
          </p>
        </div>
      </section>

      <Stats />

      {/* CINEMATIC VIDEO BREAK */}
      <section ref={videoSectionRef} className="relative w-full overflow-hidden" style={{ height: "min(90vh, 640px)" }} data-testid="cinematic-video">
        {/* Video is 120% tall, centred vertically — JS parallax shifts it ±50px as section scrolls */}
        <video
          ref={videoRef}
          src="/hylas-cinematic.mp4"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            width: "100%",
            height: "120%",
            top: "-10%",
            objectFit: "cover",
            willChange: "transform",
          }}
        />
        {/* gradient overlay — dark edges, clear center */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-transparent to-[#0A0A0A]/70" />
        <div className="absolute inset-0 bg-[#0A0A0A]/30" />
        {/* copy */}
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <span className="font-head text-[10px] uppercase tracking-[0.5em] text-primary mb-5">
            The Open Road
          </span>
          <h2 className="font-head text-4xl sm:text-5xl md:text-7xl font-bold uppercase tracking-[0.12em] text-white leading-none max-w-3xl">
            This Is<br /><span className="text-primary">Your Life</span>
          </h2>
          <p className="mt-6 text-white/65 text-base md:text-lg max-w-md leading-relaxed">
            New truck. Your lane. We handle everything else so you can focus on the road.
          </p>
          <Link
            to="/apply"
            className="mt-10 inline-flex items-center gap-3 font-head text-sm uppercase tracking-[0.25em] bg-primary text-black px-9 py-4 hover:bg-white transition-colors duration-300"
          >
            Start Your Application <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* GROSS PAY CALCULATOR */}
      <GrossPayCalculator />

      {/* FIND MY LANE QUIZ */}
      <FindMyLane />

      {/* LOCAL POSITIONS MAP */}
      <LocalPositionsMap />

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="home-benefits">
        <SectionHeading overline="Why Drive With Us" title="Built Around Drivers" subtitle="New trucks, honest pay, and home time that actually fits your life." />

        {/* Why Hylas — factual stat strip */}
        <div
          data-testid="why-hylas-stats"
          className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-border divide-x divide-y md:divide-y-0 divide-border"
        >
          {[
            { stat: "85¢+",   label: "CPM Company",      sub: "Starting rate" },
            { stat: "$1",      label: "CPM Team Seats",   sub: "Per driver" },
            { stat: "88%",     label: "Lease Revenue",    sub: "Of gross freight" },
            { stat: "2020–26", label: "Model Year Fleet", sub: "No old iron" },
            { stat: "48",      label: "States",           sub: "No area restriction" },
            { stat: "1 Day",   label: "Recruiter Callback",sub: "Same business day" },
          ].map(({ stat, label, sub }) => (
            <div key={label} className="bg-white px-5 py-6 flex flex-col gap-1">
              <span className="font-head text-2xl md:text-3xl font-bold text-[#0A0A0A] leading-none">{stat}</span>
              <span className="font-head text-[11px] uppercase tracking-[0.2em] text-primary mt-1">{label}</span>
              <span className="text-xs text-foreground/45">{sub}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-foreground/40 italic">
          Pay figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border mt-4 border border-border">
          {BENEFITS.map((b, i) => (
            <div key={b.title} className="bg-white p-8 md:p-10 hover:-translate-y-1 transition-transform duration-300">
              <span className="font-head text-xs tracking-[0.3em] text-primary">0{i + 1}</span>
              <h3 className="font-head text-xl uppercase tracking-[0.1em] mt-4 text-[#0A0A0A]">{b.title}</h3>
              <p className="mt-3 text-foreground/70 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VIDEO TESTIMONIALS */}
      <VideoTestimonials />

      {/* TESTIMONIALS */}
      <section className="bg-muted py-24 md:py-32" data-testid="home-testimonials">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading overline="From The Cab" title="Drivers Talk" center />
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white border border-border p-8">
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#0A0A0A] text-[#0A0A0A]" />)}
                </div>
                <p className="text-foreground/80 leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center gap-4 mt-7">
                  <img src={t.img} alt={`${t.name} — ${t.role} at Hylas Companion`} className="w-12 h-12 object-cover grayscale" loading="lazy" />
                  <div>
                    <p className="font-head text-sm uppercase tracking-[0.1em]">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <CtaBanner />
    </div>
  );
}

export function CtaBanner() {
  return (
    <section className="relative py-28 md:py-40 overflow-hidden" data-testid="cta-banner">
      <div className="absolute inset-0">
        <img src={IMAGES.scania} alt="Hylas Companion new truck — apply for CDL-A driver jobs today" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-primary/90" />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <h2 className="font-head text-4xl md:text-6xl font-bold uppercase tracking-[0.15em] text-[#0A0A0A]">
          Ready To Roll?
        </h2>
        <p className="mt-6 text-lg md:text-xl text-[#0A0A0A]/80 max-w-xl mx-auto">
          Apply in under two minutes. Our team calls you back fast and gets you into a new truck.
        </p>
        <Link
          to="/apply"
          data-testid="cta-apply-btn"
          className="mt-10 inline-flex items-center gap-2 font-head text-base uppercase tracking-[0.2em] bg-[#0A0A0A] text-primary px-10 py-5 hover:bg-white hover:text-[#0A0A0A] transition-colors duration-300"
        >
          Apply Now <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  );
}
